import { DATE_FORMAT_YMD_HYPHEN } from '@/constants/date-format';
import { cdnAxios } from '@/services/axiosInstances';
import type { PoiContent, ScreenSaverInfo } from '@/services/dto';
import dayjs from 'dayjs';

/**
 * S3 Cache Service for direct S3 access
 * Fetches cached data directly from S3 using the cache structure created by cron jobs
 * Cache structure: {ENVIRONMENT}/cache/{type}/{date}/{filename}
 */
export class S3CacheService {
    /**
     * Today's date (YYYY-MM-DD) in the device's local timezone
     */
    private static getLocalDateString(): string {
        return dayjs().format(DATE_FORMAT_YMD_HYPHEN);
    }

    /**
     * Get S3 cache path for axios request
     * @param cacheType - Cache type (store-data, poi-data, faq-data, screen-data)
     * @param date - Date in YYYY-MM-DD format
     * @param filename - File name (store_X.json or building_X.json)
     */
    private static getS3CachePath(cacheType: string, date: string, filename: string): string {
        const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
        return `/${environment}/cache/${cacheType}/${date}/${filename}`;
    }

    /**
     * Fetch JSON data from S3 using axios
     */
    private static async fetchJsonFromS3<T = any>(path: string): Promise<any> {
        try {
            return await cdnAxios.get<T>(path);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) {
                throw new Error(`S3 access forbidden (403). Check CDN permissions.`);
            }
            if (status === 404) {
                throw new Error(`S3 file not found (404): ${path}`);
            }

            throw error;
        }
    }

    /**
     * Get store config data directly from S3
     */
    static async getStoreConfigData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('store-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get POI contents data directly from S3
     */
    static async getPoiContentsData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('poi-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get FAQ data directly from S3
     */
    static async getFaqData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('faq-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get screen saver data directly from S3
     */
    static async getScreenSaverData(buildingId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `building_${buildingId}.json`;
        const path = this.getS3CachePath('screen-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get processed store config from cache
     */
    static async getProcessedStoreConfig(dataConfig: any): Promise<any> {
        try {
            // Get store data
            const cachedStoreConfig = await this.getStoreConfigFromCache(dataConfig.store.id);
            if (cachedStoreConfig) {
                // Add Store Config
                dataConfig.store = { ...dataConfig.store, ...cachedStoreConfig };

                // Add POI contents for each building from cache
                if (dataConfig.store.buildings) {
                    dataConfig.store.buildings = await Promise.all(
                        dataConfig.store.buildings.map(async (building: any) => {
                            const buildingPoiContents = (await this.getPoiContentsFromCache(dataConfig.store.id, building.studioBuildingId)) || [];
                            return {
                                ...building,
                                poiContents: buildingPoiContents,
                            };
                        })
                    );
                }

                // Add FAQs from cache
                dataConfig.store.faqs = await this.getFaqsFromCache(dataConfig.store.id);
            }

            // Get Screen Saver
            const floorNames = dataConfig.floor?.name;
            const floorName = floorNames?.['ko'] || floorNames?.['en'] || floorNames?.['ja'] || floorNames?.['zh-CN'] || floorNames?.['zh-TW'] || '';
            dataConfig.screenSaver = await this.getScreenSaverFromCache({
                buildingId: Number(dataConfig.building.id),
                buildingType: dataConfig.building.buildingType,
                floorName,
                orientation: dataConfig.machine.orientation,
            });

            return dataConfig;
        } catch (error) {
            console.error('Error processing store config from cache:', error);
            throw error;
        }
    }

    /**
     * Get screen saver from cache with filtering (like backend)
     */
    private static async getScreenSaverFromCache({
        buildingId,
        buildingType,
        floorName,
        orientation,
    }: {
        buildingId: number;
        buildingType: string;
        floorName: string;
        orientation: string;
    }): Promise<ScreenSaverInfo[]> {
        try {
            const data = await this.getScreenSaverData(buildingId);
            const filteredData = this.filterScreenDataByOrientationAndFloor(data, orientation, buildingType, floorName);
            return filteredData;
        } catch (error) {
            console.error('Error getting screen saver from cache:', error);
            return [];
        }
    }

    /**
     * Get store config from cache
     */
    private static async getStoreConfigFromCache(storeId: string): Promise<any> {
        try {
            const data = await this.getStoreConfigData(parseInt(storeId, 10));
            return data;
        } catch (error) {
            console.error('Error getting store config from cache:', error);
            return null;
        }
    }

    /**
     * Filter screen saver data by floor name
     */
    private static filterByFloorName(screenSavers: any[], floorName: string): any[] {
        if (!screenSavers || !Array.isArray(screenSavers)) {
            return screenSavers;
        }

        return screenSavers.filter((screenSaver) => {
            const screenFloorName = screenSaver.floorName;
            return screenFloorName === null || screenFloorName === undefined || screenFloorName === floorName;
        });
    }

    /**
     * Filter screen saver data by orientation, buildingType and floorName
     */
    private static filterScreenDataByOrientationAndFloor(
        data: any,
        orientation: string,
        buildingType: string,
        floorName: string
    ): ScreenSaverInfo[] {
        const orientationData = data[orientation] || {};
        const buildingTypeArray = orientationData[buildingType] || [];
        const filteredBuildingTypeArray = this.filterByFloorName(buildingTypeArray, floorName);
        return filteredBuildingTypeArray;
    }

    /**
     * Get POI contents from cache
     */
    private static async getPoiContentsFromCache(storeId: string, buildingId?: string): Promise<PoiContent[]> {
        try {
            const data = await this.getPoiContentsData(parseInt(storeId, 10));

            if (buildingId && data.byBuilding && data.byBuilding[buildingId]) {
                return data.byBuilding[buildingId];
            }

            return [];
        } catch (error) {
            console.error('Error getting POI contents from cache:', error);
            return [];
        }
    }

    /**
     * Get FAQs from cache
     */
    private static async getFaqsFromCache(storeId: string): Promise<any[]> {
        try {
            const storeData = await this.getFaqData(parseInt(storeId, 10));
            return storeData || [];
        } catch (error) {
            console.error('Error getting FAQs from cache:', error);
            return [];
        }
    }
}
